/**
 * Loads the full active Skills/Certifications/Proficiency Levels catalog from the same
 * AdminApi configuration documents the tenant-admin screen manages, not just the entries
 * workers currently hold. Resource Availability's filter dropdowns source their options
 * from here, so every catalog entry is selectable, even one no visible worker holds.
 */
import { useEffect, useMemo, useState } from 'react'
import { useApi } from '@/lib/api'

export interface CatalogSkill {
  id: number
  name: string
}

export interface CatalogCertification {
  id: number
  displayName: string
}

export interface CatalogProficiencyLevel {
  id: number
  name: string
}

interface UseSkillsCertificationsCatalogResult {
  loading: boolean
  loaded: boolean
  skills: CatalogSkill[]
  certifications: CatalogCertification[]
  proficiencyLevels: CatalogProficiencyLevel[]
  getSkillName: (id: number) => string | undefined
  getCertificationName: (id: number) => string | undefined
  getProficiencyLevelName: (id: number) => string | undefined
}

// AdminApi wraps a config document's entry list as [{ Data: { Value: [...] } }].
interface ConfigEnvelope<T> {
  GroupName?: string
  Data?: { Value?: T[] }
}
interface SkillEntry { Id?: number; Name?: string; IsActive?: boolean }
interface CertificationEntry {
  Id?: number
  CertificationName?: string
  CertifyingBody?: string
  Level?: string
  IsActive?: boolean
}
interface ProficiencyLevelEntry { Id?: number; Name?: string; IsActive?: boolean }

interface Catalog {
  skills: CatalogSkill[]
  certifications: CatalogCertification[]
  proficiencyLevels: CatalogProficiencyLevel[]
}

// Module-level cache: one fetch per session, shared across every component using this hook.
let catalogCache: Catalog | null = null
let loadPromise: Promise<void> | null = null

// An entry with no IsActive property is treated as active.
function isActive(entry: { IsActive?: boolean }): boolean {
  return entry.IsActive !== false
}

function activeSortedByLabel<T extends { IsActive?: boolean }>(
  entries: T[],
  labelOf: (entry: T) => string
): T[] {
  return entries
    .filter(isActive)
    .sort((a, b) => labelOf(a).localeCompare(labelOf(b)))
}

function certificationDisplayName(entry: CertificationEntry): string {
  return [entry.CertifyingBody, entry.CertificationName, entry.Level].filter(Boolean).join(' ')
}

export function useSkillsCertificationsCatalog(): UseSkillsCertificationsCatalogResult {
  const { adminApi } = useApi()
  const [catalog, setCatalog] = useState<Catalog | null>(catalogCache)
  const [loading, setLoading] = useState(!catalogCache)

  useEffect(() => {
    if (catalogCache) return
    if (!loadPromise) {
      loadPromise = Promise.all([
        adminApi.get<ConfigEnvelope<SkillEntry>[]>('/GlobalConfiguration/competency-entries/Skills'),
        adminApi.get<ConfigEnvelope<CertificationEntry>[]>('/GlobalConfiguration/competency-entries/Certifications'),
        adminApi.get<ConfigEnvelope<ProficiencyLevelEntry>[]>('/GlobalConfiguration/competency-entries/ProficiencyLevels'),
      ])
        .then(([skillsRes, certsRes, levelsRes]) => {
          const skillEntries = activeSortedByLabel(skillsRes?.[0]?.Data?.Value ?? [], e => e.Name ?? '')
          const certEntries = activeSortedByLabel(certsRes?.[0]?.Data?.Value ?? [], certificationDisplayName)
          const levelEntries = activeSortedByLabel(levelsRes?.[0]?.Data?.Value ?? [], e => e.Name ?? '')

          catalogCache = {
            skills: skillEntries
              .filter((e): e is SkillEntry & { Id: number } => e.Id !== undefined)
              .map(e => ({ id: e.Id, name: e.Name || `Skill ${e.Id}` })),
            certifications: certEntries
              .filter((e): e is CertificationEntry & { Id: number } => e.Id !== undefined)
              .map(e => ({
                id: e.Id,
                displayName: certificationDisplayName(e) || `Certification ${e.Id}`,
              })),
            proficiencyLevels: levelEntries
              .filter((e): e is ProficiencyLevelEntry & { Id: number } => e.Id !== undefined)
              .map(e => ({ id: e.Id, name: e.Name || `Level ${e.Id}` })),
          }
        })
        .catch(err => {
          // A failed load leaves the catalog empty rather than blocking the dashboard.
          console.warn('Failed to load skills/certifications/proficiency-levels catalog', err)
          catalogCache = { skills: [], certifications: [], proficiencyLevels: [] }
        })
    }
    // A promise can be `.then()`-ed any number of times, so every instance awaits the same
    // shared promise regardless of which one created it.
    loadPromise.then(() => {
      setCatalog(catalogCache)
      setLoading(false)
    })
  }, [adminApi])

  // Memoized so a caller's own useCallback/useMemo keyed on this return value (e.g. resolving a
  // label) stays stable across renders where the catalog itself hasn't changed, instead of
  // re-deriving from a fresh object identity every render.
  return useMemo(() => {
    const skills = catalog?.skills ?? []
    const certifications = catalog?.certifications ?? []
    const proficiencyLevels = catalog?.proficiencyLevels ?? []

    return {
      loading,
      loaded: catalog !== null,
      skills,
      certifications,
      proficiencyLevels,
      getSkillName: (id: number) => skills.find(s => s.id === id)?.name,
      getCertificationName: (id: number) => certifications.find(c => c.id === id)?.displayName,
      getProficiencyLevelName: (id: number) => proficiencyLevels.find(p => p.id === id)?.name,
    }
  }, [loading, catalog])
}
